/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useState } from 'react';
import { Button, Form, Card, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import { useEffect } from 'react';
import axios from 'axios';
import {
  set,
  format,
  addDays,
  parse,
  isMonday,
  isTuesday,
  isWednesday,
  isThursday,
  isFriday,
} from 'date-fns';
import _ from 'lodash';

export const CreateSubscription = ({ grades, plans, postUrl }: any) => {
  const [gradeId, setGradeId] = useState(1);
  const [planId, setPlanId] = useState(1);
  const [startTime, setStartTime] = useState('');
  const [availableStartTimes, setAvailableStartTimes] = useState([]);

  const [selectedMondayStartTimeOption, setSelectedMondayStartTimeOption] = useState(null);
  const [selectedTuesdayStartTimeOption, setSelectedTuesdayStartTimeOption] = useState(null);
  const [selectedWednesdayStartTimeOption, setSelectedWednesdayStartTimeOption] = useState(null);
  const [selectedThursdayStartTimeOption, setSelectedThursdayStartTimeOption] = useState(null);
  const [selectedFridayStartTimeOption, setSelectedFridayStartTimeOption] = useState(null);
  const [selectedStartDateOption, setSelectedStartDateOption] = useState(null);

  const [availableMondayStartTimes, setAvailableMondayStartTimes] = useState([]);
  const [availableTuesdayStartTimes, setAvailableTuesdayStartTimes] = useState([]);
  const [availableWednesdayStartTimes, setAvailableWednesdayStartTimes] = useState([]);
  const [availableThursdayStartTimes, setAvailableThursdayStartTimes] = useState([]);
  const [availableFridayStartTimes, setAvailableFridayStartTimes] = useState([]);
  const [availableStartDates, setAvailableStartDates] = useState([]);

  const [loading, setLoading] = useState(false);

  const csrfToken = (document.querySelector('[name=csrf-token]') as any)?.content;
  const fromDate = new Date();
  const toDate = addDays(new Date(), 28);

  /* API */
  const loadSubscribableStartTimes = async () => {
    setLoading(true);
    await axios
      .get('/student/api/subscribable_start_times', {
        params: {
          grade_id: gradeId,
          from: format(fromDate, 'yyyy-MM-dd HH:mm:ss'),
          to: format(toDate, 'yyyy-MM-dd HH:mm:ss'),
        },
      })
      .then((response) => {
        setAvailableStartTimes(
          response.data['subscribable_start_times'].map((time: any) =>
            format(parse(time, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx", new Date()), 'yyyy-MM-dd HH:mm:ss'),
          ),
        );
        setLoading(false);
      })
      .catch((_error) => {
        alert('読み込みに失敗しました。');
        setLoading(false);
      });
  };

  /* Utils */
  const timeToyyyyMMdd = (time: any) => {
    return format(parse(time, 'yyyy-MM-dd HH:mm:ss', new Date()), 'yyyy-MM-dd');
  };

  const timeToHHmm = (time: any) => {
    return format(parse(time, 'yyyy-MM-dd HH:mm:ss', new Date()), 'HH:mm');
  };

  const timeIsMonday = (time: any) => {
    return isMonday(parse(time, 'yyyy-MM-dd HH:mm:ss', new Date()));
  };

  const timeIsTuesday = (time: any) => {
    return isTuesday(parse(time, 'yyyy-MM-dd HH:mm:ss', new Date()));
  };

  const timeIsWednesday = (time: any) => {
    return isWednesday(parse(time, 'yyyy-MM-dd HH:mm:ss', new Date()));
  };

  const timeIsThursday = (time: any) => {
    return isThursday(parse(time, 'yyyy-MM-dd HH:mm:ss', new Date()));
  };

  const timeIsFriday = (time: any) => {
    return isFriday(parse(time, 'yyyy-MM-dd HH:mm:ss', new Date()));
  };

  /* Generate contents from state */
  const isFormValid = () => {
    return startTime && gradeId && planId;
  };

  const generateMondayStartTimes = () => {
    const availableStartTimesOnMonday = _.filter(availableStartTimes, (time: any) =>
      timeIsMonday(time),
    );
    return _.uniq(_.map(availableStartTimesOnMonday, (time: any) => timeToHHmm(time))).sort();
  };

  const generateTuesdayStartTimes = () => {
    const availableStartTimesOnTuesday = _.filter(availableStartTimes, (time: any) =>
      timeIsTuesday(time),
    );
    return _.uniq(_.map(availableStartTimesOnTuesday, (time: any) => timeToHHmm(time))).sort();
  };

  const generateWednesdayStartTimes = () => {
    const availableStartTimesOnWednesday = _.filter(availableStartTimes, (time: any) =>
      timeIsWednesday(time),
    );
    return _.uniq(_.map(availableStartTimesOnWednesday, (time: any) => timeToHHmm(time))).sort();
  };

  const generateThursdayStartTimes = () => {
    const availableStartTimesOnThursday = _.filter(availableStartTimes, (time: any) =>
      timeIsThursday(time),
    );
    return _.uniq(_.map(availableStartTimesOnThursday, (time: any) => timeToHHmm(time))).sort();
  };

  const generateFridayStartTimes = () => {
    const availableStartTimesOnFriday = _.filter(availableStartTimes, (time: any) =>
      timeIsFriday(time),
    );
    return _.uniq(_.map(availableStartTimesOnFriday, (time: any) => timeToHHmm(time))).sort();
  };

  const generateStartDates = () => {
    const availableStartDatesArray = _.filter(availableStartTimes, (time: any) => {
      const time_HH_mm = timeToHHmm(time);
      if (selectedMondayStartTimeOption) {
        return timeIsMonday(time) && time_HH_mm == selectedMondayStartTimeOption.value;
      }
      if (selectedTuesdayStartTimeOption) {
        return timeIsTuesday(time) && time_HH_mm == selectedTuesdayStartTimeOption.value;
      }
      if (selectedWednesdayStartTimeOption) {
        return timeIsWednesday(time) && time_HH_mm == selectedWednesdayStartTimeOption.value;
      }
      if (selectedThursdayStartTimeOption) {
        return timeIsThursday(time) && time_HH_mm == selectedThursdayStartTimeOption.value;
      }
      if (selectedFridayStartTimeOption) {
        return timeIsFriday(time) && time_HH_mm == selectedFridayStartTimeOption.value;
      }

      return false;
    });

    return _.uniq(_.map(availableStartDatesArray, (time: any) => timeToyyyyMMdd(time)));
  };

  const generateStartTime = () => {
    return (
      _.find(availableStartTimes, (time: any) => {
        if (selectedStartDateOption == null) return false;
        if (selectedMondayStartTimeOption) {
          return (
            timeToyyyyMMdd(time) == selectedStartDateOption.value &&
            timeToHHmm(time) == selectedMondayStartTimeOption.value
          );
        }
        if (selectedTuesdayStartTimeOption) {
          return (
            timeToyyyyMMdd(time) == selectedStartDateOption.value &&
            timeToHHmm(time) == selectedTuesdayStartTimeOption.value
          );
        }
        if (selectedWednesdayStartTimeOption) {
          return (
            timeToyyyyMMdd(time) == selectedStartDateOption.value &&
            timeToHHmm(time) == selectedWednesdayStartTimeOption.value
          );
        }
        if (selectedThursdayStartTimeOption) {
          return (
            timeToyyyyMMdd(time) == selectedStartDateOption.value &&
            timeToHHmm(time) == selectedThursdayStartTimeOption.value
          );
        }
        if (selectedFridayStartTimeOption) {
          return (
            timeToyyyyMMdd(time) == selectedStartDateOption.value &&
            timeToHHmm(time) == selectedFridayStartTimeOption.value
          );
        }
        return false;
      }) || ''
    );
  };

  /* State hooks */
  useEffect(() => {
    setStartTime('');
    if (gradeId) {
      loadSubscribableStartTimes();
    }
    setSelectedMondayStartTimeOption(null);
    setSelectedTuesdayStartTimeOption(null);
    setSelectedWednesdayStartTimeOption(null);
    setSelectedThursdayStartTimeOption(null);
    setSelectedFridayStartTimeOption(null);
    setSelectedStartDateOption(null);
  }, [gradeId]);

  useEffect(() => {
    setStartTime('');
    setAvailableMondayStartTimes(generateMondayStartTimes());
    setAvailableTuesdayStartTimes(generateTuesdayStartTimes());
    setAvailableWednesdayStartTimes(generateWednesdayStartTimes());
    setAvailableThursdayStartTimes(generateThursdayStartTimes());
    setAvailableFridayStartTimes(generateFridayStartTimes());
  }, [availableStartTimes]);

  useEffect(() => {
    setStartTime('');
    if (selectedMondayStartTimeOption != null) {
      setSelectedTuesdayStartTimeOption(null);
      setSelectedWednesdayStartTimeOption(null);
      setSelectedThursdayStartTimeOption(null);
      setSelectedFridayStartTimeOption(null);
    }
    setSelectedStartDateOption(null);
    setAvailableStartDates(generateStartDates());
  }, [selectedMondayStartTimeOption]);

  useEffect(() => {
    setStartTime('');
    if (selectedTuesdayStartTimeOption != null) {
      setSelectedMondayStartTimeOption(null);
      setSelectedWednesdayStartTimeOption(null);
      setSelectedThursdayStartTimeOption(null);
      setSelectedFridayStartTimeOption(null);
    }
    setSelectedStartDateOption(null);
    setAvailableStartDates(generateStartDates());
  }, [selectedTuesdayStartTimeOption]);

  useEffect(() => {
    setStartTime('');
    if (selectedWednesdayStartTimeOption != null) {
      setSelectedMondayStartTimeOption(null);
      setSelectedTuesdayStartTimeOption(null);
      setSelectedThursdayStartTimeOption(null);
      setSelectedFridayStartTimeOption(null);
    }
    setSelectedStartDateOption(null);
    setAvailableStartDates(generateStartDates());
  }, [selectedWednesdayStartTimeOption]);

  useEffect(() => {
    setStartTime('');
    if (selectedThursdayStartTimeOption != null) {
      setSelectedMondayStartTimeOption(null);
      setSelectedTuesdayStartTimeOption(null);
      setSelectedWednesdayStartTimeOption(null);
      setSelectedFridayStartTimeOption(null);
    }
    setSelectedStartDateOption(null);
    setAvailableStartDates(generateStartDates());
  }, [selectedThursdayStartTimeOption]);

  useEffect(() => {
    setStartTime('');
    if (selectedFridayStartTimeOption != null) {
      setSelectedMondayStartTimeOption(null);
      setSelectedTuesdayStartTimeOption(null);
      setSelectedWednesdayStartTimeOption(null);
      setSelectedThursdayStartTimeOption(null);
    }
    setSelectedStartDateOption(null);
    setAvailableStartDates(generateStartDates());
  }, [selectedFridayStartTimeOption]);

  useEffect(() => {
    setStartTime(generateStartTime());
  }, [selectedStartDateOption]);

  /* JSX itself */
  return (
    <div>
      {loading && (
        <div
          style={{
            backgroundColor: 'white',
            zIndex: 10,
            opacity: 0.5,
            height: '100vh',
            fontSize: '60px',
          }}>
          Loading...
        </div>
      )}
      <Form action={postUrl} method="post">
        <input type="hidden" name="subscription[start_time]" value={startTime} />
        <input type="hidden" name="authenticity_token" value={csrfToken} />
        <Form.Group controlId="input">
          <Form.Label className={'label'}>１．ご希望のクラスを選択してください</Form.Label>
          <Select
            name="subscription[grade_id]"
            options={grades}
            defaultValue={grades[0]}
            getOptionLabel={({ name }: any) => name}
            getOptionValue={({ id }: any) => id}
            onChange={(selectedOption: any) => {
              setGradeId(selectedOption.id);
            }}
          />
          <Card className="mt-2">
            <Card.Body>
              <p>※クラスのレベル目安・英語割合</p>
              <h6>＜Starter Class＞英語5割 日本語5割</h6>
              <p>
                楽しく英語に慣れ親しみ、コミュニケーションの大切さを身につけます。身近な英単語や日常会話を理解し、簡単に答えることができるようになります。
              </p>
              <h6>＜Middle Class＞英語7割 日本語3割</h6>
              <p>
                身近なトピックについて理解し、自分の気持ちなどを伝えるための基礎力を身につけます。好きなこと、できること、やりたいことについて相手とコミュニケーションがとれるようになります。
              </p>
              <h6>＜High Class＞英語9割　日本語1割</h6>
              <p>
                日常の生活や身の回りのトピックについて理解し、言いたいことを伝える表現力を身につけます。
                <br />
                状況を把握した上で自分の意見を述べ、相手のコミュニケーションがとれるようになります。
              </p>
              <h6>＜Upper Class＞英語10割　日本語0割</h6>
              <p>
                積み上げてきた英語力を応用し、日常生活に関するトピックについて実戦的な表現力を身につけます。
                <br />
                まとまった会話を聞いて、その中の情報を理解し、自分の意見とともに対話ができるようになります。
              </p>
            </Card.Body>
          </Card>
        </Form.Group>
        <Form.Group controlId="input">
          <div>
            <Form.Label className={'label'}>２．ご希望のコースを選択してください</Form.Label>
            <Select
              name="subscription[plan_id]"
              options={plans}
              defaultValue={plans[0]}
              getOptionLabel={({ name }: any) => name}
              getOptionValue={({ id }: any) => id}
              onChange={(selectedOption: any) => {
                setPlanId(selectedOption.id);
              }}
            />
          </div>
        </Form.Group>
        <Form.Group controlId="input">
          <div>
            <Form.Label className={'label'}>３．ご希望の曜日・時間を選択してください</Form.Label>
            <p className="text-danger">
              ※ 選択肢に表示がない時間帯は現在満席となっております。ご了承くださいませ。
            </p>
            <Row>
              <Col md={3}>
                <Form.Label className={'label'}>月曜日</Form.Label>
                <Select
                  name="monday"
                  value={selectedMondayStartTimeOption}
                  options={availableMondayStartTimes.map((v) => {
                    return { value: v, label: v };
                  })}
                  onChange={(selectedOption: any) => {
                    setSelectedMondayStartTimeOption(selectedOption);
                  }}
                />
              </Col>
              <Col md={3}>
                <Form.Label className={'label'}>火曜日</Form.Label>
                <Select
                  name="tuesday"
                  value={selectedTuesdayStartTimeOption}
                  options={availableTuesdayStartTimes.map((v) => {
                    return { value: v, label: v };
                  })}
                  onChange={(selectedOption: any) => {
                    setSelectedTuesdayStartTimeOption(selectedOption);
                  }}
                />
              </Col>
              <Col md={3}>
                <Form.Label className={'label'}>水曜日</Form.Label>
                <Select
                  name="wednesday"
                  value={selectedWednesdayStartTimeOption}
                  options={availableWednesdayStartTimes.map((v) => {
                    return { value: v, label: v };
                  })}
                  onChange={(selectedOption: any) => {
                    setSelectedWednesdayStartTimeOption(selectedOption);
                  }}
                />
              </Col>
              <Col md={3}>
                <Form.Label className={'label'}>木曜日</Form.Label>
                <Select
                  name="thursday"
                  value={selectedThursdayStartTimeOption}
                  options={availableThursdayStartTimes.map((v) => {
                    return { value: v, label: v };
                  })}
                  onChange={(selectedOption: any) => {
                    setSelectedThursdayStartTimeOption(selectedOption);
                  }}
                />
              </Col>
              <Col md={3}>
                <Form.Label className={'label'}>金曜日</Form.Label>
                <Select
                  name="friday"
                  value={selectedFridayStartTimeOption}
                  options={availableFridayStartTimes.map((v) => {
                    return { value: v, label: v };
                  })}
                  onChange={(selectedOption: any) => {
                    setSelectedFridayStartTimeOption(selectedOption);
                  }}
                />
              </Col>
            </Row>
          </div>
        </Form.Group>
        <Form.Group controlId="input">
          <div>
            <Form.Label className={'label'}>
              ４．レッスン開始の希望日を選択してください（開始日以降、同じ曜日・時間にレッスンが自動で設定されます）
            </Form.Label>
            <Select
              name="startDate"
              value={selectedStartDateOption}
              options={availableStartDates.map((v) => {
                return { value: v, label: v };
              })}
              onChange={(selectedOption: any) => setSelectedStartDateOption(selectedOption)}
            />
          </div>
        </Form.Group>
        <Form.Group controlId="input">
          {/* @ts-ignore Occurs during typescript migration. The variant does not contain brand-purple, but we have not yet confirmed which color is appropriate. */}
          <Button type="submit" variant="brand-purple" disabled={!isFormValid()}>
            申し込む
          </Button>
        </Form.Group>
      </Form>
    </div>
  );
};
